import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const TYPES = [
  { key: 'wormers', label: 'Worming' },
  { key: 'vaccines', label: 'Vaccines' },
];

const BulkHealthPage = () => {
  const navigate = useNavigate();
  const [horses, setHorses] = useState([]);
  const [type, setType] = useState('wormers');
  const [sel, setSel] = useState({});
  const [perNotes, setPerNotes] = useState({});
  const [noteOpen, setNoteOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const [remindWeeks, setRemindWeeks] = useState('8');
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ product_name: '', name: '', date_given: today, given_by: '', next_due_date: '', notes: '' });

  useEffect(() => {
    api.get('/horses').then(r => {
      const hs = r.data || [];
      setHorses(hs);
      const all = {}; hs.forEach(h => { all[h.id] = true; });
      setSel(all);
    }).catch(() => toast.error('Failed to load horses'));
  }, []);

  const picked = horses.filter(h => sel[h.id]);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (picked.length === 0) return toast.error('Pick at least one horse');
    const payload = { date_given: form.date_given, notes: form.notes || null };
    if (type === 'wormers') {
      if (!form.product_name.trim()) return toast.error('Product name required');
      payload.product_name = form.product_name.trim();
      if (remindWeeks) {
        const d = new Date(form.date_given + 'T00:00:00');
        d.setDate(d.getDate() + parseInt(remindWeeks, 10) * 7);
        payload.next_due_date = d.toISOString().slice(0, 10);
      }
    } else {
      if (!form.name.trim()) return toast.error('Vaccine name required');
      payload.name = form.name.trim();
      if (form.given_by) payload.given_by = form.given_by;
      if (form.next_due_date) payload.next_due_date = form.next_due_date;
    }
    const per_horse = {};
    picked.forEach(h => { if (perNotes[h.id]) per_horse[h.id] = { notes: [form.notes, perNotes[h.id]].filter(Boolean).join(' - ') }; });
    setSaving(true);
    try {
      const { data } = await api.post('/health/bulk', { record_type: type, horse_ids: picked.map(h => h.id), payload, per_horse });
      toast.success('Logged for ' + data.count + ' horse' + (data.count === 1 ? '' : 's'));
      navigate('/horses');
    } catch (e) { toast.error(e.response?.data?.message || 'Bulk entry failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <TopBar title="Bulk health entry" subtitle="One entry, the whole barn" />
      <div className="page-content">
        <div className="section-label">What happened?</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {TYPES.map(t => (
            <div key={t.key} className={'chip ' + (type === t.key ? 'selected' : '')} onClick={() => setType(t.key)}>{t.label}</div>
          ))}
        </div>

        <div className="card">
          {type === 'wormers' && (
            <div>
              <div className="form-group">
                <label className="form-label">Product *</label>
                <input className="form-input" placeholder="e.g. Ivermectin (Zimecterin)" value={form.product_name} onChange={set('product_name')} />
              </div>
              <div className="form-group">
                <label className="form-label">Remind again in</label>
                <select className="form-input" value={remindWeeks} onChange={e => setRemindWeeks(e.target.value)}>
                  <option value="">No reminder</option>
                  <option value="4">4 weeks</option>
                  <option value="6">6 weeks</option>
                  <option value="8">8 weeks</option>
                  <option value="12">12 weeks</option>
                </select>
              </div>
            </div>
          )}
          {type === 'vaccines' && (
            <div>
              <div className="form-group">
                <label className="form-label">Vaccine *</label>
                <input className="form-input" placeholder="e.g. Spring 5-way (EWT/Flu/Rhino)" value={form.name} onChange={set('name')} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Given by</label>
                  <input className="form-input" placeholder="e.g. Dr. Martin" value={form.given_by} onChange={set('given_by')} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Next due (optional)</label>
                  <input type="date" className="form-input" value={form.next_due_date} onChange={set('next_due_date')} />
                </div>
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Date</label>
            <input type="date" className="form-input" value={form.date_given} onChange={set('date_given')} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes (applies to all)</label>
            <input className="form-input" placeholder="Optional" value={form.notes} onChange={set('notes')} />
          </div>
        </div>

        <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Which horses? ({picked.length} of {horses.length})</span>
          <button className="btn btn-secondary btn-sm" onClick={() => {
            const all = picked.length < horses.length;
            const next = {}; horses.forEach(h => { next[h.id] = all; });
            setSel(next);
          }}>{picked.length < horses.length ? 'Select all' : 'Clear all'}</button>
        </div>
        <div className="card" style={{ padding: '6px 14px' }}>
          {horses.map(h => (
            <div key={h.id} style={{ borderBottom: '1px solid var(--border-color)', padding: '8px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!sel[h.id]} onChange={e => setSel(s => ({ ...s, [h.id]: e.target.checked }))} style={{ accentColor: 'var(--cr-primary)', width: 18, height: 18 }} />
                <span style={{ fontSize: 14, fontWeight: 500, flex: 1 }}>{h.name}</span>
                {sel[h.id] && !noteOpen[h.id] && !perNotes[h.id] && (
                  <button type="button" onClick={e => { e.preventDefault(); setNoteOpen(n => ({ ...n, [h.id]: true })); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>+ note</button>
                )}
              </label>
              {sel[h.id] && (noteOpen[h.id] || perNotes[h.id]) && (
                <input className="form-input" style={{ marginTop: 6, fontSize: 12 }} placeholder={'Just for ' + h.name}
                  value={perNotes[h.id] || ''} onChange={e => setPerNotes(p => ({ ...p, [h.id]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>

        <button className="btn btn-primary btn-full" style={{ marginTop: 14 }} disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Log for ' + picked.length + ' horse' + (picked.length === 1 ? '' : 's')}
        </button>
      </div>
    </div>
  );
};

export default BulkHealthPage;
